import { path, pathOr, map } from 'ramda'
import React from 'react'
import { useTranslation } from 'react-i18next'

import { Checkbox } from '../../../../packages/kiti/components/shapes/Marker'
import { Col, Grid } from '../../../../packages/kiti/components/structure/Grid'
import { Label, SmallText } from '../../../../packages/kiti/components/presentation/Text'
import { hasReplyValue, isWideQuestion } from './_data/rowReplies'
import {
  useBulkGridActions,
  useBulkGridColumns,
  useBulkGridRows,
  useBulkGridSelection
} from './_state/BulkGridStateContext'
import { useBulkExecutionAllowed } from '../_load/ServiceOrderLoader'
import { useBulkRowActions } from './_requests/bulkRowActions'
import DynamicInput from '../../../../components/form/DynamicInput'
import IconButton from '../../../../components/actions/IconButton'
import Panel from '../../../../packages/kiti/components/structure/Panel'
import Scroll from '../../../../packages/kiti/components/helpers/Scroll'
import Separator from '../../../../packages/kiti/components/helpers/Separator'
import WideCellModal from './WideCellModal'

const SIMPLE_COL_WIDTH = 13
const WIDE_COL_WIDTH = 9
const IDENTITY_COL_WIDTH = 15

// Célula simples: edita a resposta direto na grade, reaproveitando o mesmo
// `DynamicInput` da execução individual (sem o wrapper imperativo — aqui o
// estado é 100% controlado pela grade).
const TEXT_LIKE_TYPES = new Set(['text', 'cpf_cnpj'])

const SimpleCell = ({ serviceOrderId, reply }) => {
  const { setCellValue } = useBulkGridActions()
  const question = reply.question
  const options = pathOr([], ['options'], question)
  const masks = map(path(['pattern']), pathOr([], ['masks'], question))

  if (!reply.shouldRender) {
    return (
      <SmallText color="dark4" title="Não se aplica a esta atividade (condição de exibição não atendida)">
        —
      </SmallText>
    )
  }

  return (
    <DynamicInput
      valueType={question.type?.name}
      value={reply.value}
      allowNull={question.allowNull}
      options={options}
      masks={masks}
      integrationFieldType={question?.data?.integration?.integrationFieldType}
      integrationId={question?.data?.integration?.integrationId}
      {...(question.data || {})}
      {...(TEXT_LIKE_TYPES.has(question.type?.name) ? { simpleInput: true } : {})}
      // O picker de ativo depende de `useServiceOrder().place`, que na
      // execução em massa é um registro único representativo — não o local
      // desta linha específica. Filtrar por local aqui usaria o local errado
      // para algumas linhas, então preferimos não filtrar.
      {...(question.type?.name === 'asset' ? { filterByPlace: false } : {})}
      onChange={value => setCellValue(serviceOrderId, reply.questionId, value)}
    />
  )
}

const WideCell = ({ serviceOrderId, reply }) => {
  const { openWideEditor } = useBulkGridActions()

  if (!reply.shouldRender) {
    return (
      <SmallText color="dark4" title="Não se aplica a esta atividade (condição de exibição não atendida)">
        —
      </SmallText>
    )
  }

  // `hasReplyValue` (mesmo helper da execução individual) olha só para
  // `value` já ter sido preenchido de fato — diferente de `isValid`, que
  // também depende de campos secundários (foto/nota obrigatórios) e por
  // isso não deve decidir se a célula "já foi respondida". Isso evita dois
  // bugs: bloco vazio (`value: []`) sendo mostrado como respondido, e
  // assinatura já salva continuando a mostrar "Preencher".
  const answered = hasReplyValue(reply)
  const requiredAndMissing = !!reply.question?.required && !reply.validations?.isValid

  return (
    <Panel
      padding={0.5}
      borderColor={requiredAndMissing ? 'red' : answered ? 'darkGreen' : 'light3'}
      onClick={() => openWideEditor(serviceOrderId, reply.questionId)}
    >
      <SmallText bold color={requiredAndMissing ? 'red' : answered ? 'darkGreen' : 'dark3'} align="center">
        {answered ? 'Respondido — abrir' : 'Preencher'}
      </SmallText>
    </Panel>
  )
}

const RowIdentity = ({ serviceOrder }) => (
  <>
    <SmallText bold noMargin>
      {serviceOrder.yearlyId}
    </SmallText>
    {!!serviceOrder.name && <SmallText color="dark3">{serviceOrder.name}</SmallText>}
    {!!serviceOrder.place?.name && <SmallText color="dark3">{serviceOrder.place.name}</SmallText>}
  </>
)

// Ações por linha: concluir OU pausar (salvar sem concluir) UMA atividade
// individualmente, sem precisar esperar pelo "Enviar"/"Pausar" gerais da
// grade inteira (rodapé). Cada linha tem sua própria instância da mutation
// (loading independente por linha). Renderizadas como a última coluna da
// própria linha (alinhadas à direita, no topo — mesma posição vertical das
// demais células, como "assinatura"/"data e hora"), e não mais como um bloco
// centralizado abaixo da linha, por pedido da PO. Usam o mesmo `IconButton`
// (cores/tamanho) usado pelos botões gerais de Enviar/Pausar do rodapé
// (`../Footer.js`) — em vez de um estilo compacto próprio — para manter
// consistência visual entre as ações gerais e as individuais.
const RowActions = ({ row }) => {
  const { t } = useTranslation()
  const { completeRow, pauseRow, loading } = useBulkRowActions()
  const bulkAllowed = row.serviceOrder?.checklist?.allowBulkExecution !== false

  return (
    <Grid gutter={0.4}>
      <Col>
        <IconButton
          icon="BsStopwatch"
          label={t('pause')}
          loading={loading}
          disabled={!bulkAllowed}
          title="Pausar (salva as respostas preenchidas sem concluir)"
          onClick={() => pauseRow(row.serviceOrder.id)}
          transparent
        />
      </Col>
      <Col>
        <IconButton
          iconRight="BsCheckCircle"
          label={t('complete')}
          color="primary"
          loading={loading}
          disabled={!bulkAllowed}
          title="Concluir esta atividade"
          onClick={() => completeRow(row.serviceOrder.id)}
        />
      </Col>
    </Grid>
  )
}

const GridRow = ({ row }) => {
  const columns = useBulkGridColumns()
  const { selected, toggleRow } = useBulkGridSelection()
  const isSelected = selected.has(row.serviceOrder.id)

  return (
    <>
      <Grid gutter={0.4}>
        <Col minWidth={2}>
          <Checkbox value={isSelected} onChange={() => toggleRow(row.serviceOrder.id)} />
        </Col>
        <Col minWidth={IDENTITY_COL_WIDTH}>
          <RowIdentity serviceOrder={row.serviceOrder} />
        </Col>
        {columns.map(question => {
          const reply = row.replies.find(item => item.questionId === question.id)
          const wide = isWideQuestion(question)
          return (
            <Col key={question.id} minWidth={wide ? WIDE_COL_WIDTH : SIMPLE_COL_WIDTH}>
              {wide ? (
                <WideCell serviceOrderId={row.serviceOrder.id} reply={reply} />
              ) : (
                <SimpleCell serviceOrderId={row.serviceOrder.id} reply={reply} />
              )}
            </Col>
          )
        })}
        <Col>
          <RowActions row={row} />
        </Col>
      </Grid>
      <Separator weight={4} />
    </>
  )
}

const GridHeader = () => {
  const columns = useBulkGridColumns()
  const { allSelected, toggleAll } = useBulkGridSelection()

  return (
    <Grid gutter={0.4}>
      <Col minWidth={2}>
        <Checkbox value={allSelected} onChange={toggleAll} />
      </Col>
      <Col minWidth={IDENTITY_COL_WIDTH}>
        <Label>Atividade</Label>
      </Col>
      {columns.map(question => (
        <Col key={question.id} minWidth={isWideQuestion(question) ? WIDE_COL_WIDTH : SIMPLE_COL_WIDTH}>
          <Label>
            {question.name}
            {question.required ? '*' : ''}
          </Label>
        </Col>
      ))}
    </Grid>
  )
}

const SelectionBanner = () => {
  const { selected } = useBulkGridSelection()
  if (selected.size < 2) return false

  return (
    <>
      <Panel bgColor="orange" padding={0.8}>
        <SmallText color="white" bold>
          {selected.size} atividades marcadas — editar uma célula em uma linha marcada aplica a mesma resposta a todas
          as marcadas.
        </SmallText>
      </Panel>
      <Separator weight={4} />
    </>
  )
}

// Banner adicional à checagem de "mesmo questionário" (feita antes de abrir
// esta tela): se, mesmo assim, alguma atividade carregada tiver um
// checklist com `allowBulkExecution: false`, avisa e desabilita o "Enviar"
// do rodapé (ver `Footer.js`) — as ações por linha continuam bloqueadas
// individualmente pelo próprio backend na mutation.
const BulkNotAllowedBanner = () => {
  const allowed = useBulkExecutionAllowed()
  if (allowed) return false

  return (
    <>
      <Panel bgColor="red" padding={0.8}>
        <SmallText color="white" bold>
          Uma ou mais atividades selecionadas possuem questionários que não permitem execução em massa. Revise a seleção
          para continuar.
        </SmallText>
      </Panel>
      <Separator weight={4} />
    </>
  )
}

const BulkExecutionGrid = () => {
  const rows = useBulkGridRows()

  return (
    <div className="bulkExecutionGrid">
      <BulkNotAllowedBanner />
      <SelectionBanner />
      <Scroll height="60vh" scroll scrollX direction="column">
        <GridHeader />
        <Separator weight={6} />
        {rows.map(row => (
          <GridRow key={row.serviceOrder.id} row={row} />
        ))}
      </Scroll>
      <WideCellModal />
    </div>
  )
}

export default BulkExecutionGrid
